import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, ROUTES } from '@/helpers/constants';
import { getFullFormattedDate } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignRequestServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import { CampaignRequestFilesResponse, CampaignTableInfo } from '@/types/Files';
import styles from './FilesList.module.scss';

const COLUMNS_KEYS = {
  fileName: 'fileName',
  lastUpdate: 'lastUpdate',
};
const sorting = 'id,asc';

type FilesListProps = {
  timeZone: string;
};

const FilesList = (props: FilesListProps) => {
  const { timeZone } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { loading, setLoading } = useGlobalSpinnerContext();
  const [tableLoading, setTableLoading] = useState(false);
  const [filesList, setFilesList] = useState<CampaignTableInfo[]>([]);
  const { addToast } = useToastContext();
  const { goTo } = useGoTo();
  const fetchCampaignDocumentsList = useApiRequest(campaignRequestServices.getCampaignDocumentsList);

  const columns = [
    { name: COLUMNS_KEYS.fileName, title: t('attachments.fileName') },
    { name: COLUMNS_KEYS.lastUpdate, title: t('attachments.lastModificationDate') },
  ];

  const fetchContent = async (pageNumber: number, callSize: number, documentsList: CampaignTableInfo[]) => {
    setTableLoading(true);
    fetchCampaignDocumentsList(pageNumber, callSize, sorting)
      .then(async (res: CampaignRequestFilesResponse) => {
        documentsList.push(...res.content[0].campaigns);

        if (pageNumber + 1 < res.totalPages) {
          await fetchContent(pageNumber + 1, callSize, documentsList);
        } else {
          setTableLoading(false);
          setFilesList(documentsList);
        }
      })
      .catch((error) => {
        addToast(error.message, 'ERROR');
      })
      .finally(() => {
        setTableLoading(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    titleService.updatePageTitle(t('titles.files-list'));

    if (!loading) fetchContent(0, 200, []);
  }, [t, lang, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const prepareFilesList = useMemo(() => filesList.map((campaignInfo) => ({
    fileName: `${t('campaignRequests.campaign')} ${campaignInfo.campaignId} - ${t('attachments.files')}`,
    lastUpdate: getFullFormattedDate(campaignInfo.lastModificationDate, timeZone, 'yyyy/MM/dd HH:mm:ss'),
    onClick: () => goTo(`${ROUTES.FILES}/${campaignInfo.campaignId}`),
  })), [filesList, timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.PageContent}>
      <h1>{`${t('titles.files-list')}`}</h1>
      <TableListEditableNew
        isFiltering
        list={prepareFilesList}
        columns={columns}
        pageSizes={[10, 20, 50, 100]}
        columnSorting={[
          { columnName: COLUMNS_KEYS.fileName, direction: ASC_ORDER },
          { columnName: COLUMNS_KEYS.lastUpdate, direction: ASC_ORDER },
        ]}
        filteringStateColumns={[{ columnName: COLUMNS_KEYS.fileName, filteringEnabled: true }]}
        loading={tableLoading}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  timeZone: getSettingsInfo(state)?.timezone,
});

export default connect(mapStateToProps)(FilesList);
